"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useState } from "react"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/navigation"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  Desktop,
  Mobile,
  transitionDelayTime,
  transitionTime,
  TransitionUp,
  useInViewConfig,
} from "ui"
import Container from "ui/Container"
import { sharedColors } from "ui/theme/colors"
import { mediaQueries } from "ui/theme/mediaQueries"
import { delayTime, transitionEase } from "ui/transitions/utils"
import { extractProperty, getNonUmbracoUrl, Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
const OuterContainer = styled.div<{ $isGray?: boolean }>`
  width: 100%;
  background-color: ${({ $isGray }) =>
    $isGray ? sharedColors.mediumGrayLight : "transparent"};
`

const StyledLayout = styled.div`
  padding: 88px 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 64px;
  text-align: center;
  ${mediaQueries.mobile} {
    padding: 40px 0;
    .swiper-wrapper {
      display: flex;
      align-items: center;
    }
  }
`

const CommentBox = styled.div``

const StyledCommentTypography = styled(Typography)`
  p {
    margin: 0;
  }
`

const NumbersContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 40px;
  column-gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 64px;
  }
  width: 100%;
`

const NumberBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
`

const StyledTypography = styled.p`
  margin: 0;
  color: ${sharedColors.brandDark};
  text-align: center;
  /* font-family: var(--rag); */
  height: fit-content;
  font-size: 64px !important;
  line-height: 86%;
  letter-spacing: -2.4px;
  font-weight: 700;
  ${mediaQueries.mobile} {
    font-size: 56px !important;
  }
`

const StyledSwiperSlide = styled(SwiperSlide, {
  shouldForwardProp: (prop: string) =>
    !["$active", "$inView", "$delay"].includes(prop),
})<{
  $active: boolean
  $inView: boolean
  $delay: number
}>`
  display: flex;
  padding: 16px 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: ${(p) => (p.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inView ? "translateY(0px)" : `translateY(200px)`)};
  transition-delay: ${(p) => `${p.$delay}s`};
  border-radius: 12px;
  border: 1px solid #d1d5db;
  background-color: white;
  gap: ${(props) => (props.$active ? "16px" : "12px")};
  width: 159px;
  height: ${(props) => (props.$active ? "171px" : "119px")};
  box-shadow: ${(props) =>
    props.$active ? "0px 2px 8px 0px rgba(38, 48, 61, 0.16)" : "none"};
  &:not(:last-child) {
    margin-right: 16px !important;
  }
  transition: all 0.5s ease;
  ${mediaQueries.mobile} {
    padding: 12px;
    gap: 12px;
    min-height: ${(props) => (props.$active ? "159px" : "119px")};
    height: auto;
    h6 {
      width: 95%;
    }
  }
`

const ItemBox = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "$active",
})<{ $active: boolean; theme?: any }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  border: 1px solid #d1d5db;
  background-color: white;
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;
  transition: all ${transitionTime}s ${transitionEase};
  padding: ${(props: { $active: boolean }) =>
    props.$active ? "16px" : "16px 12px"};
  gap: ${(props: { $active: boolean }) => (props.$active ? "16px" : "12px")};
  width: ${(props: { $active: boolean }) =>
    props.$active ? "171px" : "127px"};
  height: ${(props: { $active: boolean }) =>
    props.$active ? "159px" : "119px"};
  box-shadow: ${(props: { $active: boolean }) =>
    props.$active ? "0px 2px 8px 0px rgba(38, 48, 61, 0.16)" : "none"};

  /* Apply transitions to children */
  & > img {
    transition: all ${transitionTime}s ${transitionEase};
  }
  & > * {
    transition: all ${transitionTime}s ${transitionEase};
  }

  ${({ theme }: { theme: any }) => theme.breakpoints.up("md")} {
    gap: ${(props: { $active: boolean }) => (props.$active ? "20px" : "16px")};
    width: ${(props: { $active: boolean }) =>
      props.$active ? "196px" : "144px"};
    height: ${(props: { $active: boolean }) =>
      props.$active ? "190px" : "135px"};

    /* Apply hover effects that match active state */
    &:hover,
    &:focus {
      border: 1px solid #6f747d;
      box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);
      padding: 16px;
      gap: 20px;
      width: 196px;
      min-height: 190px;
      height: auto;

      /* Update children on hover to match active state */
      & > img {
        width: 56px !important;
        height: 56px !important;
      }

      & > p,
      span {
        font-size: 1.25rem !important;
        font-weight: 600 !important;
        line-height: 1.6 !important;
      }
    }
    &:focus-visible {
      border: 1px dashed white;
      outline-offset: 2px;
      outline: 2px solid black;
    }
  }

  ${({ theme }: { theme: any }) => theme.breakpoints.up("xl")} {
    width: ${(props: { $active: boolean }) =>
      props.$active ? "224px" : "144px"};
    height: ${(props: { $active: boolean }) =>
      props.$active ? "191px" : "135px"};

    &:hover,
    &:focus {
      width: 224px;
      height: 190px;
    }
  }
`

const StyledDesktopSwiperSlide = styled(SwiperSlide, {
  shouldForwardProp: (prop: string) =>
    !["$active", "$inView", "$delay"].includes(prop),
})<{
  $active: boolean
  $inView: boolean
  $delay: number
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: ${(p) => (p.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inView ? "translateY(0px)" : `translateY(200px)`)};
  transition-delay: ${(p) => `${p.$delay}s`};
  width: auto !important;
  margin-right: 32px !important;

  ${({ theme }) => theme.breakpoints.up("xl")} {
    margin-right: 40px !important;
  }
`

const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: 193px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledDesktopSwiper = styled(Swiper)`
  width: 100%;
  height: 240px;
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
  }
  .swiper-wrapper {
    align-items: center;
  }
`

const ItemsRows = styled.div`
  display: flex;
  justify-content: center;
  margin: auto;
  gap: 32px;
  height: 210px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    align-items: center;
  }

  ${({ theme }) => theme.breakpoints.up("xl")} {
    gap: 40px;
  }
`

const StyledTitleTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$inView",
})<{ $inView: boolean }>`
  opacity: ${(p) => (p.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inView ? "translateX(0px)" : `translateX(-100vw)`)};
  transition-delay: ${delayTime}s;
`
const StyledButton = styled(Button, {
  shouldForwardProp: (prop: string) => prop !== "target",
})<{ target: any }>`
  font-size: 14px !important;
  min-width: 0 !important;
  padding: 0 !important;
  border-radius: 8px;
  min-height: 26px !important;
  min-width: 104px !important;
`

type UndStr = string | undefined

type Props = {
  data: any
  blockIndex?: number
  isHomepage?: boolean
}

export default function Numbers({ data, blockIndex, isHomepage }: Props) {
  const t = useTranslations("Homepage")
  const commentLine = extractProperty(data, "commentLine") as UndStr
  const title = extractProperty(data, "title") as UndStr
  const itemsBlock = extractProperty(data, "itemsList") || []
  const items = itemsBlock.map((i: any) => {
    const itemsList = extractProperty(i, "itemsList")

    const items = itemsList?.map((i: any) => ({
      number: extractProperty(i, "number"),
      title: extractProperty(i, "label"),
    }))
    return {
      icon: extractProperty(i, "icon"),
      title: extractProperty(i, "title"),
      buttonName: extractProperty(i, "button")?.[0]?.name,
      buttonUrl: extractProperty(i, "button")?.[0]?.url,
      buttonTarget: extractProperty(i, "button")?.[0]?.target,
      items,
    }
  }) as {
    icon: string
    title: string
    buttonName: string
    buttonUrl: string
    buttonTarget: string
    items: { number: string; title: string }[]
  }[]

  const [activeIndex, setActiveIndex] = useState(2)
  const [swiperInstance, setSwiperInstance] = useState<any>(null)
  const [desktopSwiperInstance, setDesktopSwiperInstance] = useState<any>(null)

  const { ref, inView } = useInView(useInViewConfig)
  const isGray = isHomepage && typeof blockIndex === "number" && blockIndex % 2 === 1
  return (
    <OuterContainer ref={ref} $isGray={isGray}>
      <Container>
        <StyledLayout>
          <Desktop centerHorizontally>
            <StyledTitleTypography variant="h2" $inView={inView}>
              {title}
            </StyledTitleTypography>
          </Desktop>
          <Mobile centerHorizontally>
            <StyledTitleTypography variant="h2" $inView={inView}>
              {title}
            </StyledTitleTypography>
          </Mobile>
          <StyledSwiper
            centeredSlides={true}
            slidesPerView={"auto"}
            initialSlide={2}
            onSwiper={(swiper: any) => setSwiperInstance(swiper)}
            onSlideChange={(swiper: any) => {
              setActiveIndex(swiper.activeIndex)
            }}
          >
            {items.map((el, i) => {
              const active = i === activeIndex
              const normalizedTitle = el.title.replace(/\u2028/g, " ")
              return (
                <StyledSwiperSlide
                  key={i}
                  $active={active}
                  $inView={inView}
                  $delay={transitionDelayTime * 0.5 * i}
                  onClick={() => {
                    if (swiperInstance) {
                      swiperInstance.slideTo(i)
                      setActiveIndex(i)
                    }
                  }}
                >
                  <Image
                    src={getUmbracoMediaUrl(el.icon)}
                    alt=""
                    width={active ? 56 : 40}
                    height={active ? 56 : 40}
                  />
                  {active ? (
                    <>
                      <Mobile centerHorizontally>
                        <Typography
                          component={"p"}
                          variant="body2"
                          fontWeight={700}
                          style={{ width: "95%" }}
                        >
                          {normalizedTitle}
                        </Typography>
                      </Mobile>
                      <Desktop centerHorizontally>
                        <Typography
                          variant="subtitle1"
                          style={{ width: "95%" }}
                        >
                          {normalizedTitle}
                        </Typography>
                      </Desktop>
                    </>
                  ) : (
                    <>
                      <Mobile centerHorizontally>
                        <Typography
                          variant="body2"
                          style={{ textAlign: "center", width: "95%" }}
                        >
                          {normalizedTitle}
                        </Typography>
                      </Mobile>
                      <Desktop centerHorizontally>
                        <Typography
                          variant="body3"
                          style={{ textAlign: "center", width: "95%" }}
                        >
                          {normalizedTitle}
                        </Typography>
                      </Desktop>
                    </>
                  )}
                  {active && (
                    <StyledButton
                      variant="secondary"
                      size="small"
                      target={el.buttonTarget}
                      href={getNonUmbracoUrl(el.buttonUrl)}
                      LinkComponent={Link}
                    >
                      {el.buttonName}
                    </StyledButton>
                  )}
                </StyledSwiperSlide>
              )
            })}
          </StyledSwiper>
          <Desktop>
            <StyledDesktopSwiper
              centeredSlides={true}
              slidesPerView={"auto"}
              initialSlide={2}
              onSwiper={(swiper: any) => setDesktopSwiperInstance(swiper)}
              onSlideChange={(swiper: any) => {
                setActiveIndex(swiper.activeIndex)
              }}
            >
              {items.map((el, i) => {
                const active = i === activeIndex
                return (
                  <StyledDesktopSwiperSlide
                    key={i}
                    $active={active}
                    $inView={inView}
                    $delay={transitionDelayTime * 0.5 * i}
                    onClick={() => {
                      if (desktopSwiperInstance) {
                        desktopSwiperInstance.slideTo(i)
                        setActiveIndex(i)
                      }
                    }}
                  >
                    <ItemBox
                      $active={active}
                      onClick={() => {
                        if (desktopSwiperInstance) {
                          desktopSwiperInstance.slideTo(i)
                          setActiveIndex(i)
                        }
                      }}
                    >
                      <Image
                        src={getUmbracoMediaUrl(el.icon)}
                        alt=""
                        width={active ? 56 : 40}
                        height={active ? 56 : 40}
                      />
                      {active ? (
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          role="heading"
                          tabIndex={0}
                        >
                          {el.title}
                        </Typography>
                      ) : (
                        <Typography variant="body3" role="button" tabIndex={0}>
                          {el.title}
                        </Typography>
                      )}
                      {active && (
                        <StyledButton
                          LinkComponent={Link}
                          href={getNonUmbracoUrl(el.buttonUrl)}
                          variant="secondary"
                          size="medium"
                          target={el.buttonTarget}
                        >
                          {el.buttonName}
                        </StyledButton>
                      )}
                    </ItemBox>
                  </StyledDesktopSwiperSlide>
                )
              })}
            </StyledDesktopSwiper>
          </Desktop>
          <NumbersContainer>
            {items[activeIndex].items.map((el, i) => (
              <TransitionUp
                key={i}
                $inView={inView}
                $delay={transitionDelayTime * 0.5 * i}
              >
                <NumberBox key={i} role="alert">
                  <StyledTypography>{el.number}</StyledTypography>
                  <Typography variant="body1">{el.title}</Typography>
                </NumberBox>
              </TransitionUp>
            ))}
          </NumbersContainer>
          <CommentBox>
            <StyledCommentTypography
              variant="body3"
              component="div"
              color="#6C6C6C"
              dangerouslySetInnerHTML={{ __html: commentLine }}
            />
          </CommentBox>
        </StyledLayout>
      </Container>
    </OuterContainer>
  )
}
